import { Component, inject, signal } from '@angular/core';
import { ViewDidLeave, ViewWillEnter } from '@ionic/angular';
import { combineLatest, filter, interval, map, startWith, Subject, Subscription, switchMap, tap } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { SessionService } from 'src/app/services/session.service';
import { IonContent, IonCard, IonCardTitle, IonCardSubtitle, IonCardHeader, IonImg, IonCardContent, IonHeader, IonToolbar, IonButtons, IonBackButton, IonTitle } from "@ionic/angular/standalone";
import { ForInsert } from 'src/app/types/database';
import { FileService } from 'src/app/services/file.service';
import { ModalService } from 'src/app/services/modal.service';
import { ApprovalDetailsComponent } from './approval-details/approval-details.component';
import { LoaderComponent } from "../../../ui/loader/loader.component";

@Component({
  selector: 'app-approvals',
  templateUrl: './approvals.component.html',
  styleUrls: ['./approvals.component.scss'],
  standalone: true,
  imports: [IonTitle, IonBackButton, IonButtons, IonToolbar, IonHeader, IonCardContent, IonImg, IonCardHeader, IonCardSubtitle, IonCardTitle, IonCard, IonContent, LoaderComponent]
})
export class ApprovalsComponent implements ViewWillEnter, ViewDidLeave {
  sessionService = inject(SessionService);
  apiService = inject(ApiService);
  fileService = inject(FileService);
  modalService = inject(ModalService);

  loading = signal(false);
  initialized = signal(false);
  reload$ = new Subject<void>();
  timedReload$ = interval(8_000);
  model = signal<EntityApproval[] | undefined | null>(undefined);
  model$ = combineLatest([
    this.sessionService.sessionChanged$,
    this.reload$.pipe(startWith(null)),
    this.timedReload$.pipe(startWith(null)),
  ]).pipe(
    filter(([session]) => !!session),
    tap(() => !this.model() && this.loading.set(true)),
    switchMap(() => this.apiService.get<EntityApproval[]>('/admin/get-all-pages-pending-approval')),
    tap(() => this.initialized.set(true)),
    tap(() => this.loading.set(false)),
    map(model => model.data)
  );
  subscription = new Subscription();

  ionViewWillEnter(): void {
    this.init();
  }

  ionViewDidLeave(): void {
    this.subscription.unsubscribe();
    this.initialized.set(false);
  }

  init() {
    if (this.initialized()) return;
    if (this.subscription.closed) {
      this.subscription = new Subscription();
    }
    const loadSubscription = this.model$.subscribe(model => this.setModel(model));
    this.subscription.add(loadSubscription);
    this.initialized.set(true);
  }

  setModel(model: EntityApproval[] | null | undefined) {
    model?.forEach(m => {
      if (m.profilePhoto) {
        m.profilePhoto = this.fileService.getFullObjectUrl('profile-photo', m.profilePhoto);
      }
      if (m.avatar) {
        m.avatar = this.fileService.getFullObjectUrl('avatar', m.avatar);
      }
    });
    this.model.set(model);
  }

  async openEntity(entityId: number) {
    const MODAL_ID = 'approval-details';
    const tookAction = await this.modalService.open(ApprovalDetailsComponent, {
      canDismiss: true,
      componentProps: {
        entityId,
        modalId: MODAL_ID
      },
      handle: true,
      id: MODAL_ID,
    });
    if (tookAction) {
      this.reload$.next();
    }
  }
}

export type EntityApproval = {
  name: string;
  username: string;
  website: string | null;
  description: string | null;
  phoneNumber: string | null;
  email: string | null;
  id: number;
  avatar: string | null;
  hideFromSearch: boolean | null;
  profilePhoto: string | null;
  addressText: string | null;
} & ForInsert<'address'>;